import { useEffect, useState, useCallback, useRef } from 'react';
import {
  rdStyles, rdAddStyle, rdPrompt, rdCutout, rdRestyle, rdSave, rdGenerate,
  listMockups, listProducts, getCode, setCode, clearCode, lockMessage,
} from '../api/lionjob';
import { processImage, toDataUrl } from '../lib/chromaKey';

// REDESIGN — quy trình 3 bước, học từ extension matrixty nhưng gọi API chính thức:
//   Bước 0 (tuỳ chọn): bóc hoạ tiết khỏi ảnh sản phẩm — bỏ nếp vải, méo phối cảnh
//   Bước 1: vẽ lại theo phong cách mới (ẢNH→ẢNH), khoá chủ thể & bố cục
//   Bước 2: tách nền + phóng to NGAY TRÊN TRÌNH DUYỆT (không tốn tiền AI)
// Chưa có khoá OpenAI vẫn dùng được: soạn prompt rồi copy dán vào ChatGPT.

const PRODUCTS = ['t-shirt', 'sweatshirt', 'mug', 'tumbler', 'ornament', 'poster', 'tote bag', 'blanket', 'sticker'];

export default function RedesignPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [lockErr, setLockErr] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const [mode, setMode] = useState('restyle');   // restyle = ảnh→ảnh · create = chữ→ảnh
  const [styles, setStyles] = useState([]);
  const [canGen, setCanGen] = useState(false);
  const [styleId, setStyleId] = useState('');

  // ảnh gốc
  const [src, setSrc] = useState(null);          // { dataUrl, name }
  const [showPick, setShowPick] = useState(false);
  const [mockups, setMockups] = useState([]);
  const fileRef = useRef(null);

  // cài đặt
  const [applyOn, setApplyOn] = useState('t-shirt');
  const [extra, setExtra] = useState('');
  const [n, setN] = useState(2);
  const [upscale, setUpscale] = useState(2);
  const [autoBg, setAutoBg] = useState(true);
  const [chroma, setChroma] = useState(true);

  // chữ→ảnh
  const [title, setTitle] = useState('');
  const [variant, setVariant] = useState('');
  const [niche, setNiche] = useState('');

  const [prompt, setPrompt] = useState('');
  const [results, setResults] = useState([]);    // { raw, out, width, height, reason, saved }
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await rdStyles({});
      setStyles(d.rows); setCanGen(d.canGenerate); setUnlocked(true);
    } catch (e) {
      const m = e.response?.data?.message || e.message || 'Không kết nối được backend';
      if (e.response?.status === 403) { clearCode(); setUnlocked(false); }
      setLockErr(lockMessage(e)); setMsg('Lỗi: ' + m);
    }
  }, []);
  useEffect(() => { if (getCode()) load(); /* eslint-disable-next-line */ }, []);

  // đổi chế độ -> chọn lại phong cách đúng nhóm
  useEffect(() => {
    const fit = styles.filter((s) => s.mode === mode);
    if (fit.length && !fit.some((s) => s.id === styleId)) setStyleId(fit[0].id);
  }, [mode, styles, styleId]);

  async function unlock(e) {
    e?.preventDefault();
    const c = codeInput.trim(); if (!c) { setLockErr('Nhập mã code'); return; }
    setLockErr(''); setCode(c); setUnlocking(true);
    try { await load(); } finally { setUnlocking(false); }
  }

  // ---- chọn ảnh gốc ----
  async function pickFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    try { setSrc({ dataUrl: await toDataUrl(f), name: f.name }); setResults([]); setMsg('✓ Đã nạp ảnh gốc'); }
    catch (err) { setMsg('Lỗi đọc ảnh: ' + err.message); }
    e.target.value = '';
  }
  async function openMockups() {
    setShowPick(true);
    try { const d = await listMockups({ limit: 40 }); setMockups(d.rows); }
    catch (e) { setMsg('Lỗi tải kho: ' + (e.response?.data?.message || e.message)); }
  }
  async function pickMockup(m) {
    setShowPick(false);
    try {
      // tải qua cùng origin -> canvas không bị chặn đọc điểm ảnh
      const r = await fetch(m.url); const b = await r.blob();
      setSrc({ dataUrl: await toDataUrl(b), name: m.title || 'mockup' }); setResults([]);
      setMsg('✓ Đã lấy ảnh từ kho mockup');
    } catch (e) { setMsg('Không tải được ảnh: ' + e.message); }
  }

  // ---- Bước 0: bóc hoạ tiết ----
  async function doCutout() {
    if (!src) { setMsg('Chọn ảnh gốc trước'); return; }
    setBusy('cutout'); setMsg('Đang bóc hoạ tiết khỏi ảnh sản phẩm…');
    try {
      const r = await rdCutout({ imageB64: src.dataUrl });
      const first = r.data?.saved?.[0];
      if (first) {
        const res = await fetch(first.url); const b = await res.blob();
        setSrc({ dataUrl: await toDataUrl(b), name: 'cutout' });
        setMsg('✓ ' + r.message + ' — ảnh gốc đã thay bằng bản đã bóc');
      } else setMsg('✓ ' + r.message);
    } catch (e) { setMsg('Lỗi bóc hoạ tiết: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(''); }
  }

  // ---- Bước 1 + 2: vẽ lại rồi tách nền ngay trên trình duyệt ----
  async function doRestyle() {
    if (!src) { setMsg('Chọn ảnh gốc trước'); return; }
    if (!styleId) { setMsg('Chọn phong cách'); return; }
    setBusy('restyle'); setResults([]); setMsg(`Đang vẽ lại ${n} bản… (có thể mất vài phút)`);
    try {
      const d = await rdRestyle({ imageB64: src.dataUrl, styleId, applyOn, extra, n, chroma });
      setPrompt(d.prompt);
      const out = [];
      for (const im of d.images) {
        const raw = im.b64 ? `data:image/png;base64,${im.b64}` : im.url;
        if (!raw) continue;
        try {
          const p = await processImage(raw, { autoBg, upscale });
          out.push({ raw, out: p.dataUrl, width: p.width, height: p.height, reason: p.reason, saved: null });
        } catch (err) {
          out.push({ raw, out: raw, width: 0, height: 0, reason: 'Không xử lý được: ' + err.message, saved: null });
        }
      }
      setResults(out);
      setMsg(`✓ ${d.message} — đã tách nền ${out.filter((x) => /Đã tách/.test(x.reason)).length}/${out.length}`);
    } catch (e) { setMsg('Lỗi vẽ lại: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(''); }
  }

  // ---- chữ→ảnh ----
  async function doPrompt() {
    setBusy('prompt');
    try {
      const body = mode === 'restyle'
        ? { mode: 'restyle', styleId, applyOn, extra, chroma }
        : { mode: 'create', styleId, extra, productTitle: title, variant, niche, chroma };
      const d = await rdPrompt(body);
      setPrompt(d.prompt); setMsg('✓ Đã soạn prompt' + (d.canGenerate ? '' : ' — copy dán vào ChatGPT/Gemini'));
    } catch (e) { setMsg('Lỗi: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(''); }
  }
  async function doCreate() {
    if (!prompt) { setMsg('Soạn prompt trước'); return; }
    setBusy('create'); setResults([]); setMsg('Đang vẽ… (có thể mất vài phút)');
    try {
      const r = await rdGenerate({ prompt, n });
      setResults((r.data?.saved || []).map((s) => ({ raw: s.url, out: s.url, reason: 'Đã lưu kho', saved: s })));
      setMsg('✓ ' + r.message);
    } catch (e) { setMsg('Lỗi: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(''); }
  }

  async function saveOne(i) {
    const r = results[i]; if (!r || r.saved) return;
    try {
      const st = styles.find((s) => s.id === styleId);
      const res = await rdSave({ imageB64: r.out, title: `Redesign · ${st?.name || ''} · ${src?.name || ''}`.slice(0, 150) });
      setResults((rs) => rs.map((x, j) => (j === i ? { ...x, saved: res.data } : x)));
      setMsg('✓ ' + res.message);
    } catch (e) { setMsg('Lỗi lưu: ' + (e.response?.data?.message || e.message)); }
  }
  const dl = (r, i) => { const a = document.createElement('a'); a.href = r.out; a.download = `redesign-${i + 1}.png`; a.click(); };
  const copy = () => { navigator.clipboard.writeText(prompt); setMsg('✓ Đã copy prompt'); };

  if (!unlocked) {
    return (
      <div className="rd-root"><style>{CSS}</style>
        <div className="rd-lock"><div className="rd-lockCard">
          <div className="rd-lockIco">🦁</div><h2>Redesign</h2>
          <p className="rd-lockSub">Khu vực chỉ dành cho Giám đốc. Nhập mã code để mở.</p>
          <form onSubmit={unlock}>
            <input className="rd-lockInput" type="password" autoFocus placeholder="Mã code" disabled={unlocking}
              value={codeInput} onChange={(e) => setCodeInput(e.target.value)} />
            <button className="rd-btn primary" type="submit" disabled={unlocking} style={{ width: '100%', marginTop: 10 }}>
              {unlocking ? '⏳ Đang kết nối…' : 'Mở khóa'}</button>
          </form>
          {lockErr && <div className="rd-lockErr">⚠ {lockErr}</div>}
        </div></div>
      </div>
    );
  }

  const shown = styles.filter((s) => s.mode === mode);

  return (
    <div className="rd-root"><style>{CSS}</style>
      <div className="rd-head">
        <div>
          <h2>Redesign</h2>
          <div className="rd-sub">{msg || 'Đổi phong cách thiết kế có sẵn, hoặc vẽ mới từ mô tả sản phẩm.'}</div>
        </div>
        <div className="rd-sp" />
        <span className={'rd-mode ' + (canGen ? 'on' : '')}>
          {canGen ? '⚡ Sinh ảnh tự động: BẬT' : '✎ Chỉ soạn prompt (chưa có OPENAI_API_KEY)'}
        </span>
      </div>

      <div className="rd-seg">
        <button className={'rd-segbtn' + (mode === 'restyle' ? ' on' : '')} onClick={() => setMode('restyle')}>
          🔄 Đổi phong cách <small>ảnh → ảnh</small></button>
        <button className={'rd-segbtn' + (mode === 'create' ? ' on' : '')} onClick={() => setMode('create')}>
          ✨ Vẽ mới <small>chữ → ảnh</small></button>
      </div>

      <div className="rd-wrap">
        <div className="rd-col">
          {mode === 'restyle' ? (
            <div className="rd-card">
              <h3><b>1</b> Ảnh gốc</h3>
              {src ? (
                <div className="rd-srcbox">
                  <img src={src.dataUrl} alt="" />
                  <div>
                    <div className="rd-srcname">{src.name}</div>
                    <div className="rd-row" style={{ marginTop: 8 }}>
                      <button className="rd-btn sm" onClick={() => { setSrc(null); setResults([]); }}>Đổi ảnh</button>
                      <button className="rd-btn sm" onClick={doCutout} disabled={!canGen || !!busy}
                        title={canGen ? 'Bóc hoạ tiết khỏi ảnh sản phẩm, bỏ nếp vải và méo phối cảnh' : 'Cần khoá OpenAI'}>
                        {busy === 'cutout' ? '⏳…' : '✂️ Bóc hoạ tiết'}</button>
                    </div>
                    <div className="rd-hint" style={{ marginTop: 6 }}>Ảnh chụp sản phẩm (áo, cốc…) nên bóc hoạ tiết trước để bỏ nếp vải.</div>
                  </div>
                </div>
              ) : (
                <div className="rd-row">
                  <button className="rd-btn" onClick={openMockups}>🖼 Lấy từ kho mockup</button>
                  <button className="rd-btn" onClick={() => fileRef.current?.click()}>⤒ Tải ảnh lên</button>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickFile} />
                </div>
              )}
            </div>
          ) : (
            <div className="rd-card">
              <h3><b>1</b> Sản phẩm</h3>
              <input className="rd-fld wide" placeholder="Mô tả sản phẩm…" value={title} onChange={(e) => setTitle(e.target.value)} />
              <div className="rd-row">
                <input className="rd-fld" style={{ flex: 1 }} placeholder="Loại SP" value={variant} onChange={(e) => setVariant(e.target.value)} />
                <input className="rd-fld" style={{ flex: 1 }} placeholder="Niche" value={niche} onChange={(e) => setNiche(e.target.value)} />
              </div>
            </div>
          )}

          <div className="rd-card">
            <h3><b>2</b> Phong cách <span className="rd-cnt">{shown.length}</span></h3>
            <div className="rd-styles">
              {shown.map((s) => (
                <button key={s.id} className={'rd-style' + (styleId === s.id ? ' on' : '')} onClick={() => setStyleId(s.id)}>
                  <b>{s.name}</b><span>{(s.promptReady || '').slice(0, 78)}…</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rd-card">
            <h3><b>3</b> Cài đặt</h3>
            <textarea className="rd-fld wide" rows={2} placeholder="Yêu cầu thêm (không bắt buộc)"
              value={extra} onChange={(e) => setExtra(e.target.value)} />
            <div className="rd-row" style={{ marginTop: 8 }}>
              {mode === 'restyle' && (
                <label className="rd-lbl">In lên
                  <select className="rd-fld" value={applyOn} onChange={(e) => setApplyOn(e.target.value)}>
                    {PRODUCTS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select></label>
              )}
              <label className="rd-lbl">Số bản
                <input className="rd-fld num" type="number" min={1} max={4} value={n}
                  onChange={(e) => setN(Math.max(1, Math.min(4, Number(e.target.value) || 1)))} /></label>
              <label className="rd-lbl">Phóng to
                <select className="rd-fld" value={upscale} onChange={(e) => setUpscale(Number(e.target.value))}>
                  {[1, 2, 3, 4].map((x) => <option key={x} value={x}>{x}×</option>)}
                </select></label>
            </div>
            <label className="rd-chk"><input type="checkbox" checked={autoBg} onChange={(e) => setAutoBg(e.target.checked)} />
              Tự tách nền ở trình duyệt (miễn phí)</label>
            <label className="rd-chk"><input type="checkbox" checked={chroma} onChange={(e) => setChroma(e.target.checked)} />
              Ép AI tô nền xanh <span className="rd-swatch" /> <code>#00b140</code> để tách chắc chắn</label>
            <div className="rd-hint">Bảo AI “nền trong suốt” thì nó hay trả nền trắng đặc. Nền xanh đơn sắc thì tách bằng thuật toán, chắc 100%.</div>

            <div className="rd-row" style={{ marginTop: 12 }}>
              <button className="rd-btn" onClick={doPrompt} disabled={!!busy}>{busy === 'prompt' ? '⏳…' : '✦ Xem prompt'}</button>
              {mode === 'restyle' ? (
                <button className="rd-btn primary" onClick={doRestyle} disabled={!canGen || !!busy || !src}>
                  {busy === 'restyle' ? '⏳ Đang vẽ…' : '🎨 Vẽ lại'}</button>
              ) : (
                <button className="rd-btn primary" onClick={doCreate} disabled={!canGen || !!busy || !prompt}>
                  {busy === 'create' ? '⏳ Đang vẽ…' : '🎨 Sinh ảnh'}</button>
              )}
            </div>
            {!canGen && <div className="rd-hint" style={{ marginTop: 6 }}>Chưa có khoá → copy prompt dán vào ChatGPT/Gemini.</div>}
          </div>
        </div>

        <div className="rd-col">
          {prompt && (
            <div className="rd-card">
              <h3>Prompt gửi AI</h3>
              <pre className="rd-out">{prompt}</pre>
              <button className="rd-btn sm" style={{ marginTop: 8 }} onClick={copy}>⧉ Copy</button>
            </div>
          )}
          <div className="rd-card" style={{ minHeight: 160 }}>
            <h3>Kết quả</h3>
            {results.length === 0 ? (
              <div className="rd-empty">
                {mode === 'restyle'
                  ? 'Chọn ảnh gốc + phong cách rồi bấm Vẽ lại.'
                  : 'Nhập mô tả sản phẩm, xem prompt rồi bấm Sinh ảnh.'}
              </div>
            ) : (
              <div className="rd-imgs">
                {results.map((r, i) => (
                  <figure key={i} className="rd-fig">
                    <div className="rd-canvas"><img src={r.out} alt="" /></div>
                    <figcaption>
                      <div className="rd-reason">{r.reason}{r.width ? ` · ${r.width}×${r.height}` : ''}</div>
                      <div className="rd-row">
                        <button className="rd-btn sm" onClick={() => dl(r, i)}>⤓ Tải</button>
                        <button className="rd-btn sm" onClick={() => saveOne(i)} disabled={!!r.saved}>
                          {r.saved ? '✓ Đã lưu kho' : '＋ Lưu vào kho'}</button>
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showPick && (
        <div className="rd-modal" onClick={() => setShowPick(false)}>
          <div className="rd-box" onClick={(e) => e.stopPropagation()}>
            <h3>Chọn ảnh từ kho mockup</h3>
            <div className="rd-picks">
              {mockups.map((m) => (
                <button key={m.id} className="rd-pick" onClick={() => pickMockup(m)}>
                  <img src={m.url} alt="" /><span>{m.title || m.domain}</span>
                </button>
              ))}
              {mockups.length === 0 && <div className="rd-empty">Kho chưa có ảnh. Dùng extension 🖼 để nhặt ảnh trước.</div>}
            </div>
            <button className="rd-btn" style={{ marginTop: 10 }} onClick={() => setShowPick(false)}>Đóng</button>
          </div>
        </div>
      )}
    </div>
  );
}

const CSS = `
.rd-root{color:var(--text);padding-bottom:30px}
.rd-head{display:flex;gap:10px;align-items:flex-start;margin-bottom:12px;flex-wrap:wrap}
.rd-head h2{margin:0;font-size:20px}
.rd-sub{color:var(--muted);font-size:12.5px;margin-top:3px;max-width:72ch}
.rd-sp{flex:1}
.rd-mode{font-size:11.5px;color:var(--muted);border:1px solid var(--border);border-radius:20px;padding:4px 11px}
.rd-mode.on{color:var(--ok);border-color:var(--ok)}
.rd-btn{background:var(--panel);color:var(--text);border:1px solid var(--border);padding:7px 12px;border-radius:9px;cursor:pointer;font-size:13px;white-space:nowrap}
.rd-btn.sm{padding:4px 10px;font-size:12px}
.rd-btn:hover:not(:disabled){border-color:var(--accent)}
.rd-btn:disabled{opacity:.45;cursor:not-allowed}
.rd-btn.primary{background:var(--accent);border-color:var(--accent);color:#fff;font-weight:600}

.rd-seg{display:inline-flex;border:1px solid var(--border);border-radius:11px;overflow:hidden;background:var(--panel);margin-bottom:14px}
.rd-segbtn{border:0;background:transparent;color:var(--muted);padding:9px 16px;font:inherit;font-size:13px;font-weight:600;cursor:pointer;display:flex;gap:7px;align-items:baseline}
.rd-segbtn+.rd-segbtn{border-left:1px solid var(--border)}
.rd-segbtn small{font-weight:400;font-size:11px;opacity:.8}
.rd-segbtn.on{background:var(--accent);color:#fff}

.rd-wrap{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:14px}
@media (max-width:980px){.rd-wrap{grid-template-columns:1fr}}
.rd-col{display:flex;flex-direction:column;gap:14px;min-width:0}
.rd-card{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:13px 15px}
.rd-card h3{margin:0 0 10px;font-size:12px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);display:flex;align-items:center;gap:8px}
.rd-card h3 b{background:var(--accent);color:#fff;width:18px;height:18px;border-radius:50%;display:grid;place-items:center;font-size:11px;font-family:var(--font-mono)}
.rd-cnt{font-family:var(--font-mono);background:var(--input);border-radius:20px;padding:1px 7px;margin-left:auto}
.rd-row{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.rd-lbl{display:flex;flex-direction:column;gap:3px;font-size:10px;letter-spacing:.07em;text-transform:uppercase;color:var(--muted);font-weight:600}
.rd-fld{border:1px solid var(--border);background:var(--input);color:var(--text);font:inherit;font-size:12.5px;padding:7px 10px;border-radius:8px}
.rd-fld.wide{width:100%;margin-bottom:6px;resize:vertical}
.rd-fld.num{width:66px}
.rd-chk{display:flex;align-items:center;gap:7px;font-size:12.5px;color:var(--muted);cursor:pointer;margin-top:8px}
.rd-swatch{width:11px;height:11px;border-radius:3px;background:#00b140;display:inline-block;border:1px solid var(--border)}
.rd-hint{font-size:11.5px;color:var(--muted);line-height:1.55;margin-top:6px}
code{background:var(--input);border-radius:4px;padding:1px 5px;font-family:var(--font-mono);font-size:11px}

.rd-srcbox{display:flex;gap:12px;align-items:flex-start}
.rd-srcbox img{width:110px;height:110px;object-fit:contain;border:1px solid var(--border);border-radius:9px;background:var(--input);flex:none}
.rd-srcname{font-weight:600;font-size:13px;word-break:break-all}

.rd-styles{display:grid;grid-template-columns:1fr;gap:6px;max-height:250px;overflow:auto}
.rd-style{text-align:left;border:1px solid var(--border);background:var(--input);border-radius:9px;padding:8px 10px;cursor:pointer;font:inherit;color:var(--text);display:flex;flex-direction:column;gap:2px}
.rd-style:hover{border-color:var(--accent)}
.rd-style.on{border-color:var(--accent);box-shadow:0 0 0 1px var(--accent)}
.rd-style b{font-size:12.5px}
.rd-style span{font-size:11px;color:var(--muted);line-height:1.4}

.rd-out{background:var(--input);border:1px solid var(--border);border-radius:9px;padding:11px 12px;font-family:var(--font-mono);font-size:11px;line-height:1.6;white-space:pre-wrap;word-break:break-word;max-height:300px;overflow:auto;margin:0}
.rd-empty{color:var(--muted);font-size:12.5px;padding:26px 6px;text-align:center}
.rd-imgs{display:grid;grid-template-columns:repeat(auto-fill,minmax(170px,1fr));gap:10px}
.rd-fig{margin:0}
/* ô caro để nhìn rõ phần nền đã trong suốt */
.rd-canvas{border:1px solid var(--border);border-radius:9px;overflow:hidden;
  background-image:linear-gradient(45deg,var(--input) 25%,transparent 25%),linear-gradient(-45deg,var(--input) 25%,transparent 25%),linear-gradient(45deg,transparent 75%,var(--input) 75%),linear-gradient(-45deg,transparent 75%,var(--input) 75%);
  background-size:14px 14px;background-position:0 0,0 7px,7px -7px,-7px 0}
.rd-canvas img{width:100%;display:block}
.rd-fig figcaption{padding:6px 2px 0;display:flex;flex-direction:column;gap:5px}
.rd-reason{font-size:10.5px;color:var(--muted);font-family:var(--font-mono)}

.rd-modal{position:fixed;inset:0;background:rgba(8,11,20,.72);display:flex;align-items:center;justify-content:center;padding:24px;z-index:1000}
.rd-box{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:16px 18px;max-width:680px;width:100%;max-height:84vh;overflow:auto}
.rd-box h3{margin:0 0 10px;font-size:14px}
.rd-picks{display:grid;grid-template-columns:repeat(auto-fill,minmax(110px,1fr));gap:8px}
.rd-pick{border:1px solid var(--border);background:var(--input);border-radius:9px;padding:5px;cursor:pointer;font:inherit;color:var(--text)}
.rd-pick:hover{border-color:var(--accent)}
.rd-pick img{width:100%;aspect-ratio:1;object-fit:cover;border-radius:6px;display:block}
.rd-pick span{font-size:10.5px;color:var(--muted);display:block;margin-top:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

.rd-lock{display:flex;justify-content:center;padding:60px 20px}
.rd-lockCard{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:28px 26px;width:340px;text-align:center}
.rd-lockIco{font-size:38px;line-height:1}.rd-lockCard h2{margin:8px 0 4px}
.rd-lockSub{color:var(--muted);font-size:13px;margin:0 0 16px}
.rd-lockInput{width:100%;background:var(--input);color:var(--text);border:1px solid var(--border);border-radius:10px;padding:10px 12px;font-size:14px;text-align:center;letter-spacing:2px}
.rd-lockErr{color:var(--danger);font-size:12px;margin-top:10px}
`;
